import type { CounterPrinted, PrintNotice } from "../../api/types";
import { t } from "../../i18n";

/**
 * What the till says after the server sent something to the printers (prototype toasts, blok I):
 * "Keukenbon → Pizza-printer (80 mm), Pita vooraan (80 mm)", "Kassabon → Kassaprinter toog (80 mm) ×2",
 * "Kassalade open (contant)". The printers come from the server's answer, so the till says exactly
 * what was sent. Nothing is said when nothing was printed (a shop without printers).
 */

type Notices = readonly PrintNotice[] | undefined;

/** "Pizza-printer (80 mm), Pita vooraan (80 mm) ×2"; `withCopies` false for the copies of a receipt. */
function printerList(notices: readonly PrintNotice[], withCopies = true): string {
  return notices
    .map((notice) => t.print.printer(notice.printer, notice.paper, withCopies ? notice.copies : 1))
    .join(", ");
}

const isDrawer = (notice: PrintNotice) => notice.kind === "drawer";

/** A sale, an order sent to the kitchen or a payment: the kitchen, the receipt, the drawer (prototype order). */
export function printToasts(prints: Notices): string[] {
  if (!prints || prints.length === 0) return [];
  const kitchen = prints.filter((notice) => notice.kind === "kitchen" && notice.ticket === "new");
  const copies = prints.filter((notice) => notice.kind === "copy");
  const receipts = prints.filter((notice) => notice.kind === "receipt");
  return [
    kitchen.length > 0 ? t.print.kitchen(printerList(kitchen)) : null,
    // prototype: a copy of the receipt is named without "×n"
    copies.length > 0 ? t.print.copy(printerList(copies, false)) : null,
    receipts.length > 0 ? t.print.receipt(printerList(receipts)) : null,
    prints.some(isDrawer) ? t.print.drawer : null,
  ].filter((text): text is string => text !== null);
}

/**
 * A table went to the kitchen and nothing printed there because no kitchen printer is set up: said in
 * red, never silently (Papermoon, 08/10/2026: "keukenbonnen worden niet afgedrukt").
 */
export function unroutedToast(prints: Notices): string | null {
  const missing = (prints ?? []).find((notice) => notice.kind === "unrouted");
  return missing === undefined ? null : t.print.unrouted(missing.title);
}

/** Did the kitchen get something (a ticket or a copy)? "Tafel 7 bewaard · bon naar keuken". */
export function toKitchen(prints: Notices): boolean {
  return (prints ?? []).some((notice) => notice.kind === "kitchen" || notice.kind === "copy");
}

/**
 * An edit (prototype saveEdit): the drawer for cash paid on top or handed back, then BIJBESTELLING and
 * GESCHRAPT with what changed (or the changed copy of the receipt), then "Nieuwe kassabon X3K · totaal
 * € 24,10". Without a new receipt: "Bestelling X3K bijgewerkt · totaal € 24,10".
 */
export function editToasts(
  prints: Notices,
  changes: { added: readonly string[]; removed: readonly string[] },
  code: string,
  total: string,
): string[] {
  const notices = prints ?? [];
  const has = (test: (notice: PrintNotice) => boolean) => notices.some(test);
  const texts: string[] = [];
  if (has(isDrawer)) texts.push(t.print.drawer);
  if (has((notice) => notice.kind === "copy")) {
    texts.push(
      t.print.copyChanged(
        [
          changes.added.length > 0 ? t.print.plus(changes.added.join(", ")) : null,
          changes.removed.length > 0 ? t.print.minus(changes.removed.join(", ")) : null,
        ]
          .filter((text): text is string => text !== null)
          .join(" · "),
      ),
    );
  } else {
    if (has((notice) => notice.ticket === "extra")) texts.push(t.print.extra(changes.added.join(", ")));
    if (has((notice) => notice.ticket === "removed")) texts.push(t.print.removed(changes.removed.join(", ")));
  }
  texts.push(
    has((notice) => notice.kind === "receipt") ? t.print.newReceipt(code, total) : t.editing.saved(code, total),
  );
  return texts;
}

/** A cancellation: the cash back out of the drawer, GEANNULEERD to the stations. */
export function cancelToasts(prints: Notices): string[] {
  const notices = prints ?? [];
  const cancelled = notices.filter((notice) => notice.ticket === "cancelled");
  return [
    notices.some(isDrawer) ? t.print.drawer : null,
    cancelled.length > 0 ? t.print.cancelled(printerList(cancelled)) : null,
  ].filter((text): text is string => text !== null);
}

/** 🖨 printed again: "Bon X3K afgedrukt op Kassaprinter toog", "3 bonnen afgedrukt op Kassaprinter toog". */
export function reprintToast(prints: readonly PrintNotice[], code: string): string | null {
  if (prints.length === 0) return null;
  const printers = [...new Set(prints.map((notice) => notice.printer))].join(", ");
  return prints.length === 1 ? t.print.reprinted(code, printers) : t.print.reprintedMany(prints.length, printers);
}

/** At the counter: "X-rapport → Kassaprinter toog", "Afrekenbon Mehmet → Kassaprinter toog". */
export function counterToast(printed: CounterPrinted): string | null {
  return printed === null ? null : t.print.to(printed.title, printed.printer);
}
